import React from 'react';
import { styled } from '@linaria/react';
import { motion } from 'framer-motion';

import { BREAKPOINTS } from '@/constants';

import CategoryPill from '@/components/CategoryPill';
import VisuallyHidden from '@/components/VisuallyHidden';

import data from './data';

const DATA_ENTRIES = Object.entries(data);

interface Props {
  handleClick: (operator: string) => void;
  prefersReducedMotion: boolean;
  animateSuggestions: boolean;
}

const FullList = ({
  handleClick,
  prefersReducedMotion,
  animateSuggestions,
}: Props) => {
  const animationProps = animateSuggestions
    ? {
        initial: 'hidden',
        animate: 'visible',
        variants: {
          hidden: {
            opacity: 0,
            transition: {
              when: 'afterChildren',
            },
          },
          visible: {
            opacity: 1,
            transition: {
              when: 'beforeChildren',
              staggerChildren: 0.02,
              delay: 0.5,
            },
          },
        },
      }
    : {};

  return (
    <MatchSuggestionsWrapper
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <Label>Or, pick one:</Label>{' '}
      <Pills {...animationProps}>
        {DATA_ENTRIES.map(([operator, operatorMetadata], index) => (
          <motion.li
            key={index}
            variants={{
              hidden: prefersReducedMotion
                ? { opacity: 1, y: 0 }
                : { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 },
            }}
          >
            <CategoryPill onClick={() => handleClick(operator)}>
              {operator}
              <VisuallyHidden>
                {' '}
                ({operatorMetadata.label})
              </VisuallyHidden>
            </CategoryPill>
          </motion.li>
        ))}
      </Pills>
    </MatchSuggestionsWrapper>
  );
};

const MatchSuggestionsWrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  margin-top: 96px;
  margin-left: 16px;
  margin-right: 16px;
  margin-bottom: 48px;

  @media ${BREAKPOINTS.mdAndLarger} {
    margin-inline: -16px;
  }
`;

const Label = styled.span`
  display: block;
  flex: 1;
  font-size: 1rem;
  color: var(--color-gray-900);
  margin-bottom: 16px;
  animation: fadeIn 400ms 600ms both;
`;

const Pills = styled(motion.ul)`
  flex: 3.5;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  padding: 0;
  list-style-type: none;

  button {
    font-family: var(--font-family-mono);
  }

  @media ${BREAKPOINTS.smAndSmaller} {
    /*
      Increase spacing to make it less easy to accidentally tap
      the wrong operator
    */
    li {
      padding: 4px;
    }
  }
`;

export default React.memo(FullList);
